import { useState } from 'react'
import PropTypes from 'prop-types'
import Scrollspy from 'react-scrollspy'
import classNames from 'classnames'

import useDocumentScrollThrottled from 'hooks/useDocumentScrollThrottled'

import InstagramCount from 'components/InstagramCount'
import TiktokCount from 'components/TiktokCount'
import ModelContent from 'components/ModelContent'
// import ModelPdfButton from '../ModelPdfButton';
import DownloadIcon from './download.svg'

import s from './ModelGallery.module.css'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { LIVE_SITE_CMS } from '@constants'

const ModelGallery = ({
  fallbackImg,
  instagram,
  tiktok,
  firstName,
  lastName,
  galleries,
  props,
}) => {
  const router = useRouter()
  const { site } = router.query
  const { books, videos, url, activeSection, bookers, bio, slug } = props
  const [showMenu, setShowMenu] = useState(false)
  const [emailsVisible, setEmailsVisible] = useState(false)

  useDocumentScrollThrottled((callbackData) => {
    const windowPosition = window.innerHeight * 0.4
    setShowMenu(window.pageYOffset > windowPosition)
  })

  const handleLinkClick = (targetSection) => {
    const targetElement = document.getElementById(targetSection)
    if (targetElement)
      targetElement.scrollIntoView({
        behavior: 'smooth',
      })
  }

  function toggleEmails(e) {
    e.preventDefault()
    setEmailsVisible(true)
  }

  // Forcing the character case because client doesn't want to change in CMS.
  const foreName = `${firstName?.toLowerCase() || ''}`
  const surnameName = `${lastName?.toLowerCase() || ''}`

  // This here is a very important data structure as it's what we use to put together a lot of the gallery content for a model
  // Annoyingly, it seems that two of the public_galleries entries are not pluralised in the way that the old galleries were
  // They are 'runway' and 'polaroid'. So these need changing to their plurals below (which is a little annoying)

  const annoyingSwitchType = {
    runway: 'runways',
    polaroid: 'polaroids',
  }
  const annoyingSwitchTypeName = {
    Runway: 'Runways',
    Polaroid: 'Polaroids',
  }
  const allContent = [
    {
      type: 'bio',
      typeName: 'Bio',
      bio: bio,
      scrollSpyId: 'bio',
      isVisibleOnDesktop: false,
    },
    ...galleries.map((gallery) => {
      return {
        ...gallery,
        type: annoyingSwitchType[gallery.type] ?? gallery.type,
        typeName: annoyingSwitchTypeName[gallery.typeName] ?? gallery.typeName,
        scrollSpyId: gallery.type,
        isVisibleOnDesktop: true,
      }
    }),
    ...books?.map((book) => {
      return {
        type: 'books',
        typeName: book.name,
        assets: book.assets,
        slug: book.slug,
        scrollSpyId: `book_${book.slug}`,
        isVisibleOnDesktop: true,
      }
    }),
  ]

  const getContentItem = (contentItem) => {
    const portfolioLink = `${LIVE_SITE_CMS}/api/portfolio.php?agency=${site}&model=${slug}&action=portfolio`
    return (
      <h2
        key={contentItem.scrollSpyId}
        className={
          contentItem.isVisibleOnDesktop ? s.linkWrapper : s.linkWrapperHidden
        }
      >
        <button
          type='button'
          onClick={() => handleLinkClick(contentItem.scrollSpyId)}
          // to={`${url}`}
          className={s.link}
        >
          {contentItem.typeName}
        </button>
        {contentItem.type === 'portfolio' && (
          <Link href={portfolioLink}>
            <a>
              <span className={s.portfolioLink}>
                <Image
                  src={DownloadIcon}
                  alt="Download portfolio"
                  width={15}
                  height={15}
                />
              </span>
            </a>
          </Link>
        )}
      </h2>
    )
  }

  return (
    <div className={s.container}>
      <div
        className={classNames(s.menu, {
          [s.menuVisible]: showMenu,
          [s.menuHidden]: !showMenu,
        })}
      >
        <h2 className={s.title}>
          <span>{foreName}</span>
          {lastName && lastName !== '0' && <span>{surnameName}</span>}
        </h2>
        <div className={s.links}>
          <Scrollspy
            items={allContent.map((contentItem) => contentItem.scrollSpyId)}
            offset={-200}
            currentClassName={s.linkActive}
            componentTag='div'
          >
            {allContent.map((contentItem) => getContentItem(contentItem))}
            {bookers?.length > 0 && (
              <div>
                <h2 className={s.linkWrapper}>
                  <button
                    type='button'
                    className={classNames(s.link, s.enquire, {
                      [s.linkActive]: emailsVisible === true,
                    })}
                    onClick={(e) => toggleEmails(e)}
                    onBlur={() => setEmailsVisible(false)}
                  >
                    Enquire
                  </button>
                </h2>
                <div
                  className={classNames(s.emails, {
                    [s.emailsVisible]: emailsVisible === true,
                  })}
                >
                  {bookers.map(({ acfTeamMembers }) => (
                    <a
                      key={acfTeamMembers?.email}
                      href={`mailto:${bookers
                        .map((e) => e.acfTeamMembers?.email)
                        .join(',')}?subject=${firstName}${
                        lastName ? `%20${lastName}` : ''
                      }%20-%20Enquiry&body=https://www.selectmodel.com${url}`}
                      className={s.email}
                    >
                      {acfTeamMembers?.email}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Scrollspy>
          {/* <ModelPdfButton id={id} /> */}
          <InstagramCount instagram={instagram} />
          <TiktokCount tiktok={tiktok} />
        </div>
      </div>
      <div className={s.content}>
        <ModelContent
          activeSection={activeSection}
          fallbackImg={fallbackImg}
          allContent={allContent}
          {...props}
        />
      </div>
    </div>
  )
}

ModelGallery.propTypes = {
  fallbackImg: PropTypes.bool,
  instagram: PropTypes.array,
  tiktok: PropTypes.array,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  props: PropTypes.object,
}
ModelGallery.defaultProps = {
  fallbackImg: false,
  instagram: [],
  tiktok: [],
  firstName: '',
  lastName: '',
  props: {},
}

export default ModelGallery
